import * as React from "react";
import { useContext } from "react";
import ky from "ky";
import { parseError, ApiError, getStandardHeaders } from "../../apiUtils";
import { SingleLineForm } from "./SingleLineForm";

import { useToken } from "../../utils/hooks";
import { UserContext } from "../../crud/user/context";
import { Button } from "../../components/ui/button";

type PatchUserResult = { error?: ApiError };

const submitAccessKey = async (
  token: string,
  userId: number,
  gitAccessToken: string | null,
): Promise<PatchUserResult> => {
  try {
    const result = await ky.patch(`/api/v1/users/${userId}/access_token/`, {
      json: {
        git_access_token: gitAccessToken, // eslint-disable-line @typescript-eslint/naming-convention
      },
      headers: getStandardHeaders(),
    });
    await result.json();
    return { error: undefined };
  } catch (err) {
    return { error: parseError(err) };
  }
};

type FormShape = {
  gitAccessToken: string;
};

type EditState =
  | { type: "none" }
  | { type: "editing" }
  | { type: "submitting" }
  | { type: "error"; error: ApiError };

type Props = {
  onSuccessfulSubmit: () => void;
  description?: React.ReactNode;
};

export const GithubKeyForm = ({
  onSuccessfulSubmit,
  description,
}: Props): React.ReactElement => {
  const { user } = useContext(UserContext);
  const token = useToken();

  const [hasToken, setHasToken] = React.useState<boolean>(
    user.profile.hasGitAccessToken ?? false,
  );

  const [editState, setEditState] = React.useState<EditState>({ type: "none" });

  // Track the original value to revert to when canceling
  const [originalValue, setOriginalValue] = React.useState<string>("");

  const handleRemoveButtonClick = async () => {
    const res = await submitAccessKey(token, user.id, null);
    if (res.error) {
      setEditState({ type: "error", error: res.error });
      return;
    }
    setHasToken(false);
    setOriginalValue(""); // Clear the original value when removing
    onSuccessfulSubmit();
  };

  const handleCancel = () => {
    setEditState({ type: "none" });
  };

  const handleSubmit = async ({ gitAccessToken }: FormShape) => {
    setEditState({ type: "submitting" });
    const res = await submitAccessKey(token, user.id, gitAccessToken);
    if (res.error) {
      setEditState({ type: "error", error: res.error });
      return;
    }
    setHasToken(true);
    setOriginalValue(gitAccessToken);
    onSuccessfulSubmit();
    setEditState({ type: "none" });
  };

  let errorMessage = "";
  if (editState.type === "error") {
    errorMessage = "An unknown error occurred";
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-2 items-start">
        <SingleLineForm
          formActive={editState.type !== "none"}
          submitting={editState.type === "submitting"}
          error={editState.type === "error"}
          errorMessage={errorMessage}
          formOptions={{
            defaultValues: {
              gitAccessToken: originalValue,
            },
            mode: "onBlur",
          }}
          fieldName="gitAccessToken"
          inputType="password"
          value={originalValue}
          handleCancel={handleCancel}
          handleSubmit={handleSubmit}
          className="flex-1"
        />
        <Button
          onClick={handleRemoveButtonClick}
          size="default"
          className={`max-w-[150px] ${hasToken ? "" : "hidden"}`}
          variant="outline"
        >
          Remove
        </Button>
      </div>
    </div>
  );
};
